/** utility functions for manipulating the shipment data. */
import dayjs from "dayjs";
import { IMessage } from "../types";
import { BSON } from "realm-web"
import { useDataStore } from "./dataStore";
import Compressor from 'compressorjs';

export const getActiveChatMessages = (messages: Array<IMessage>) => {
    const activeMessages: Array<IMessage> = [];
    for (const message of messages) {
        if (message.deleted === false) {
            activeMessages.push(message);
        }
    }
    return activeMessages;
};

/**
 * Converts a stored Date object back to the equivalent performance.now() value.
 */
export function getPerformanceNow(createdAtDate: Date) {
    const now = new Date();
    const elapsedMillis = now.getTime() - createdAtDate.getTime(); // Elapsed time in milliseconds
    const performanceNowEquivalent = performance.now() - elapsedMillis; // Adjusting performance.now() with elapsed time
    return performanceNowEquivalent;
}


/**
 * Converts performance.now() to a formatted time string "Hh:Mm:Ss --"
 * @returns The formatted time string
 */
export function getFormattedTime() {
    // Get the current date and time
    const now = new Date();

    // Calculate the current timestamp and elapsed time in milliseconds
    const baseTime = now.getTime() - performance.now();

    // Create a Date object from the base time plus performance.now()
    const currentDate = new Date(baseTime + performance.now());

    // Format the date using dayjs
    const formattedTime = dayjs(currentDate).format('hh:mm:ss A');

    return formattedTime;
}

export const sortMessagesByCreatedAt = (messages: Partial<IMessage>[]) => {
    //@ts-ignore
    return messages.sort((a, b) => new Date(a.created_at as Date) - new Date(b.created_at as Date));
};

const sortMessagesByPerformanceNow = (messages: Partial<IMessage>[]) => {
    //@ts-ignore
    return messages.sort((a, b) => getPerformanceNow(b.created_at as Date) - getPerformanceNow(a.created_at as Date));
};


/** function ensures that tokens when passed through the url are encoded correctly and that '+' are returned as is and not replaced with ' ' which is a white space making the token invalid */
const replaceSpaces = (str: string | null) => {
    return str?.replace(/\s/g, "+");
};

export const fetchUrlTOken = () => {
    const params = new URLSearchParams(window.location.search);
    const token = replaceSpaces(params.get("token"));
    const env = params.get("env");
    return { token, env };
}

export type SalesContext = { shipments: string[]; role: "SALES_REP" | "CUSTOMER" };

/**
 * Sales mode (customer ↔ assigned sales rep) is declared in the launch URL —
 * `role=SALES_REP|CUSTOMER` plus `shipment=X` or `shipments=A,B,C` — the same
 * way warehouse scope is. The RC token authenticates the URL as a whole; the
 * server holds the session to exactly the scope declared at handshake.
 *
 * Backward compatibility: links minted before the RC-token switch carry the
 * scope inside a signed JWT instead of URL params, so when the URL declares
 * no role the token payload is decoded as a fallback (display only — the
 * server verifies the signature and enforces the scope independently).
 */
export const getSalesContext = (): SalesContext | null => {
    const params = new URLSearchParams(window.location.search);
    const role = params.get("role")?.toUpperCase();
    if (role === "SALES_REP" || role === "CUSTOMER") {
        const shipments: string[] = [
            ...(params.get("shipment") ? [params.get("shipment")] : []),
            ...(params.get("shipments")?.split(",") ?? [])
        ]
            .map((value) => value?.trim().toUpperCase())
            .filter((value, index, arr) => value && arr.indexOf(value) === index);

        if (shipments.length) {
            return { shipments, role };
        }
        return null;
    }

    // Legacy JWT link: scope lives in the token claims
    try {
        const { token } = fetchUrlTOken();
        const payloadPart = token?.split(".")[1];
        if (!payloadPart) return null;
        const payload = JSON.parse(atob(payloadPart.replace(/-/g, "+").replace(/_/g, "/")));
        const scope = payload?.scope;
        if (scope?.role !== "SALES_REP" && scope?.role !== "CUSTOMER") return null;

        const shipments: string[] = [
            ...(scope?.shipment ? [scope.shipment] : []),
            ...(Array.isArray(scope?.shipments) ? scope.shipments : [])
        ].filter((value, index, arr) => value && arr.indexOf(value) === index);

        if (shipments.length) {
            return { shipments, role: scope.role };
        }
    } catch {
        // not a JWT / no scope — not sales mode
    }
    return null;
};

/** utility  function to extract the driver and warehouse(s) from the query string */
export const getUrlParams = () => {
	const params = new URLSearchParams(window.location.search);
	const driverId = params.get('driverId')?.toUpperCase();
	const driverid = params.get('driverid')?.toUpperCase();
	const driver = params.get('driver')?.toUpperCase();
	const chatId = params.get('chatId');

	// Legacy context param (pre-restructure contract): which warehouse the
	// launch is about. It never restricts visibility.
	const warehouse = (params.get('warehouse') || params.get('warehouseId'))?.toUpperCase();

	// NEW-contract scope list: `warehouses=VDC1,EAST` — the session's entire
	// warehouse scope; anything not named here never shows in the UI and is
	// refused by the server. Absent on legacy launches, which then behave
	// exactly as before the restructure.
	const warehouses = (params.get('warehouses') ?? '')
		.split(',')
		.map((entry) => entry.trim().toUpperCase())
		.filter(Boolean);

	const shipment = params.get('shipment')?.toUpperCase();
	const language = params.get('lang')?.toLowerCase();
	const sidebarEnabled = params.get('sidebarEnabled')

	return {
		chatId,
		driver: driverId || driverid || driver,
		warehouse: warehouse || warehouses[0],
		warehouses,
		shipment: shipment || '',
		language: language || 'en',
		sidebarEnabled
	};
};


/** utility function to extract and return driver and warehouse from a URL content */
export const getDriverAndWarehouseV2 = () => {
    const params = new URLSearchParams(window.location.search);

    // Extract the first part of the query string
    const firstParam = params.keys().next().value;
    const [driver, warehouse, chatId] = firstParam ? firstParam.split('-') : ["", "", ""];

    if (!driver || !warehouse) {
        // return getDriverAndWarehouseV1();
    } else {
        return {
            driver: driver?.toUpperCase() || "",
            warehouse: warehouse?.toUpperCase() || "",
        };
    }
};


/** utility function to curb/prevent duplicate messages being saved to the message-store using string comparison 🚀 */
export const curbRedundancyV2 = (message: Partial<IMessage>, messageStore: Array<Partial<IMessage>>) => {
    const stringifiedMessageId = new BSON.ObjectId(message._id).toString();

    if (!messageStore || messageStore.length === 0) return message;

    // Check for duplicate message
    for (let msg = 0; msg < Math.min(5, messageStore.length); msg++) {
        const storeMessageId = new BSON.ObjectId(messageStore[msg]._id).toString();

        if (stringifiedMessageId === storeMessageId) {
            return messageStore.filter(msg => msg !== null);
        }
    }

    // If no duplicates were found, return message
    return messageStore;
};

/** utility function to clear deleted messages from the message-store using string comparison 🚀 */
export const clearDeletedMessages = (messageId: any, messageStore: Array<Partial<IMessage>>) => {
    const stringifiedId = new BSON.ObjectId(messageId).toString();

    // Return a new array without the message that matches the stringifiedId
    return messageStore.filter(message => {
        // Convert message._id to a string for comparison
        const messageIdString = new BSON.ObjectId(message._id).toString();
        // Filter out the message with the matching ID
        return messageIdString !== stringifiedId;
    });
};


/** utility function to countdown user session and execute a function when user session expires. */
export const startSessionCountdown = (execFn: () => void) => {
    const countdownKey = 'countdown-timer';
    const countdownDuration = 28 * 60 * 1000; // 28 minutes in milliseconds
    const interval = 1000; // 1 second interval for the countdown

    let countdownStart = sessionStorage.getItem(countdownKey);

    if (!countdownStart) {
        //@ts-ignore
        countdownStart = Date.now();
        sessionStorage.setItem(countdownKey, countdownStart);
    }

    // Function to calculate the remaining time
    const getRemainingTime = () => {
        const now = Date.now();
        //@ts-ignore
        const elapsed = now - countdownStart;
        return countdownDuration - elapsed;
    };

    const updateCountdown = () => {
        const remainingTime = getRemainingTime();

        if (remainingTime <= 0) {
            // console.log('28 minutes have elapsed.');
            execFn()
            sessionStorage.removeItem(countdownKey);
            clearInterval(countdownInterval);
        } else {
            // Optionally update UI with remaining time, e.g., console.log remaining time
            const minutes = Math.floor(remainingTime / (60 * 1000));
            const seconds = Math.floor((remainingTime % (60 * 1000)) / 1000);
            console.log(`${minutes} minutes and ${seconds} seconds remaining`);
        }
    };

    // Set up the interval to update the countdown every second
    const countdownInterval = setInterval(updateCountdown, interval);
};


export function formaAppLanguage(language: string) {
    switch (language) {
        case 'es':
            return { id: 'es', title: 'Spanish', flag: 'ES' }
        case 'ar':
            return { id: 'ar', title: 'Arabic', flag: 'AR' }
        case 'fr':
            return { id: 'fr', title: 'French', flag: 'FR' }
        case 'zh':
            return { id: 'zh', title: 'Chinese', flag: 'ZH' }
        default:
            return { id: 'en', title: 'English', flag: 'US' }
    }
}

export async function beforeUploading(files: FileList) {
    const compressAndSaveImage = (file: File) => {
        return new Promise<void>((resolve) => {
            new Compressor(file, {
                quality: 0.5,
                maxWidth: 1200,
                maxHeight: 1200,
                success(result) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        useDataStore.getState().setSelectedImages(reader.result as string);
                        resolve();
                    };
                    reader.readAsDataURL(result);
                },
                error(err) {
                    console.error('Compression error:', err.message);
                    resolve();
                }
            });
        });
    };

    const compressPromises = Array.from(files).map(compressAndSaveImage);
    await Promise.all(compressPromises);
};